import { type FC } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'react-relay/legacy.js';
import { SharedRelatedItemsSection } from '../../shared/SharedRelatedItemsSection/SharedRelatedItemsSection.tsx';
import { peopleAlsoBrowsed } from '../../shared/sharedMessages.jsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { type BuySharedPersonalizedSearchItems_viewer$key } from './__generated__/BuySharedPersonalizedSearchItems_viewer.graphql.ts';
import { type BuySharedPersonalizedSearchItems_itemSearch$key } from './__generated__/BuySharedPersonalizedSearchItems_itemSearch.graphql.ts';
import { type BuySharedPersonalizedSearchItems_buyPageItemRecommendations$key } from './__generated__/BuySharedPersonalizedSearchItems_buyPageItemRecommendations.graphql.ts';
import { type BuySharedPersonalizedSearchItems_user$key } from './__generated__/BuySharedPersonalizedSearchItems_user.graphql.ts';

type Props = {
    viewer: BuySharedPersonalizedSearchItems_viewer$key;
    itemSearch: BuySharedPersonalizedSearchItems_itemSearch$key;
    buyPageItemRecommendations:
        | BuySharedPersonalizedSearchItems_buyPageItemRecommendations$key
        | null
        | undefined;
    user: BuySharedPersonalizedSearchItems_user$key;
    isMobile: boolean;
    isLoading?: boolean;
};

export const BuySharedPersonalizedSearchItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    buyPageItemRecommendations: buyPageItemRecommendationsRef,
    user: userRef,
    isMobile,
    isLoading,
}) => {
    const viewer = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_viewer on Viewer {
                ...SharedRelatedItemsSection_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_itemSearch on ItemSearchQueryConnection {
                ...SharedRelatedItemsSection_itemSearch
                personalizedSearchItems(first: 28) {
                    edges {
                        node {
                            item {
                                ...SharedRelatedItemsSection_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const buyPageItemRecommendations = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_buyPageItemRecommendations on BuyPageItemRecommendations {
                personalizationSearchItems(first: 28) {
                    ...SharedRelatedItemsSection_item
                }
            }
        `,
        buyPageItemRecommendationsRef
    );

    const user = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_user on User {
                ...SharedRelatedItemsSection_user
            }
        `,
        userRef
    );

    const { personalizedSearchItems } = itemSearch;
    const items = (
        buyPageItemRecommendations?.personalizationSearchItems ||
        personalizedSearchItems?.edges?.map(edge => edge?.node?.item) ||
        []
    ).filter(filterFalsy);

    return (
        <SharedRelatedItemsSection
            viewer={viewer}
            itemSearch={itemSearch}
            items={items}
            user={user}
            header={peopleAlsoBrowsed}
            initialRowCount={isMobile ? 2 : 1}
            list="Buy personalized search items"
            isLoading={isLoading}
        />
    );
};
